import { NotFoundError } from '@ember-data/adapter/error';
import { action } from '@ember/object';
import Route from '@ember/routing/route';
import { service } from '@ember/service';

import window from 'ember-window-mock';

import { registerJsURL } from 'register-partners-js/constants/hosts';

export default class WelcomeRoute extends Route {
  @service store;
  @service router;
  @service session;
  @service intl;
  @service localeManager;
  @service currentUser;
  @service segment;
  @service zendeskLocalization;
  @service zendeskWidget;
  @service launchdarkly;
  @service apiFlowManager;

  async model({ redirection_id }) {
    try {
      let redirection = await this.store.findRecord('redirection-token', redirection_id);
      let negotiatedLocale = this.localeManager.negotiateLocale(redirection.locale);
      let user = this.store.createRecord('user', {
        email: redirection.ownerEmail,
        locale: negotiatedLocale,
      });
      return { redirection, user };
    } catch (error) {
      if (error instanceof NotFoundError) {
        this.apiFlowManager.isPartnersService = false;
        let redirection = await this.store.findRecord('register-redirection-token', redirection_id);
        let negotiatedLocale = this.localeManager.negotiateLocale(redirection.locale);
        let user = this.store.createRecord('user', {
          email: redirection.ownerEmail,
          locale: negotiatedLocale,
        });
        return { redirection, user };
      } else {
        throw error;
      }
    }
  }

  afterModel(model) {
    let {
      flowType,
      legalFlow,
      ownerEmail,
      ownerFirstName,
      partnerName,
      organizationName,
      organizationId,
      organizationLegalCountry,
      companyCreationFromScratch,
      locale,
    } = model.redirection;

    if (flowType === 'manual' && legalFlow === 'company_creation') {
      window.location.replace(registerJsURL);
      throw new Error('TransitionAborted');
    }

    this.segment.identify({
      email: ownerEmail,
      firstName: ownerFirstName,
      partnerName: companyCreationFromScratch ? null : partnerName,
      organizationName,
      organizationId,
      is_company_creation_from_scratch: companyCreationFromScratch,
    });

    this.zendeskLocalization.organizationCountry = organizationLegalCountry;
    this.zendeskWidget.selectCountry(organizationLegalCountry);

    if (!this.currentUser.user) {
      sessionStorage?.setItem('logged-out-locale-code', locale);
      let negotiatedLocale = this.localeManager.negotiateLocale(locale);
      return this.localeManager.loadAndSetLocale(negotiatedLocale);
    }
  }

  @action
  error(error) {
    if (error.errors) {
      if (error instanceof NotFoundError) {
        this.router.transitionTo('react', '404');
        return false;
      }
    }
    return true;
  }

  redirect(model) {
    let {
      id,
      flowType,
      flowUserType,
      organizationLegalCountry,
      organizationId,
      companyCreationFromScratch,
      partnerName,
    } = model.redirection;

    if (flowType === 'not_eligible') {
      return this.router.transitionTo('welcome.not-eligible', id);
    }

    if (flowUserType === 'other') {
      return this.router.transitionTo('welcome.not-owner', id);
    }

    if (partnerName?.toLowerCase() === 'mollie' && !this.session.isAuthenticated) {
      return this.router.transitionTo('welcome.email-confirmation', id);
    }

    if (['norman', 'thomas charlier - beglaubigt.de'].includes(partnerName?.toLowerCase())) {
      this.segment.track('onboarding_partner_register_started', {
        country_selected: organizationLegalCountry,
        situation: 'company_creation',
        under_registration: true,
        organization_id: organizationId,
        partner_name: partnerName,
        is_company_creation_from_scratch: true,
      });

      if (!this.session.isAuthenticated) {
        return this.router.transitionTo('welcome.email-confirmation', id);
      }

      let newUrl = new URL(registerJsURL);
      newUrl.searchParams.set('skip', 'true');

      return window.location.replace(newUrl);
    }

    if (companyCreationFromScratch) {
      this.segment.track('onboarding_partner_register_started', {
        country_selected: organizationLegalCountry,
        situation: 'company_creation',
        under_registration: true,
        organization_id: organizationId,
        partner_name: null,
        is_company_creation_from_scratch: true,
      });

      if (this.session.isAuthenticated) {
        return this.router.transitionTo(
          'registration.start',
          this.apiFlowManager.isPartnersService
            ? model.redirection.registration.id
            : model.redirection.partnerRegistration.id
        );
      }

      return this.router.transitionTo('welcome.email-confirmation', id);
    }

    this.session.prohibitAuthentication('authenticated');
  }
}
